/**
 * Crew availability schema for the Planner Crew view.
 *
 * Owned by Agent B (Wave 8). The shape mirrors the data we expect from the
 * crew-load / field-user endpoints, plus a derived `availability` field that
 * marks each crew member's status for a given calendar day.
 *
 * Patterns follow MIGRATION_PLAYBOOK §3.5: every union is expressed via
 * `z.union([z.literal(...)])` so `z.input == z.output` and react-hook-form
 * Resolver types stay clean. No `.optional()` on schema fields used in forms.
 */
import { z } from 'zod';

/**
 * Crew member skill (what kind of scaffold work they can do).
 * Maps to ScaffoldTaskType enum on the backend: 1=Erect, 2=Modify, 3=Dismantle,
 * 4=Inspection, 5=Maintenance, 6=General. We treat "All-rounder" as the synthetic
 * UI value when a worker carries every skill — surfaced as a filter chip only.
 */
export const crewSkillSchema = z.union([
  z.literal('Erect'),
  z.literal('Modify'),
  z.literal('Dismantle'),
  z.literal('Inspection'),
  z.literal('Maintenance'),
  z.literal('General'),
  z.literal('AllRounder'),
]);
export type CrewSkill = z.infer<typeof crewSkillSchema>;

/**
 * Availability status for a single (crew member, day) pair.
 * - `Available` — free for assignment
 * - `Booked` — already on at least one PlannerActivity for that date
 * - `Off` — non-working day (e.g. weekend or rostered off)
 * - `OnLeave` — annual / sick / other approved leave
 */
export const crewAvailabilityStatusSchema = z.union([
  z.literal('Available'),
  z.literal('Booked'),
  z.literal('Off'),
  z.literal('OnLeave'),
]);
export type CrewAvailabilityStatus = z.infer<typeof crewAvailabilityStatusSchema>;

/**
 * Filter chip values for the skill chip group at the top of the Crew page.
 * `All` resets the chip group (no skill narrowing applied).
 */
export const crewSkillFilterSchema = z.union([
  z.literal('All'),
  z.literal('Erect'),
  z.literal('Modify'),
  z.literal('Dismantle'),
  z.literal('AllRounder'),
]);
export type CrewSkillFilter = z.infer<typeof crewSkillFilterSchema>;

export const crewSkillFilterOptions: ReadonlyArray<{
  value: CrewSkillFilter;
  label: string;
}> = [
  { value: 'All', label: 'All' },
  { value: 'Erect', label: 'Erect' },
  { value: 'Modify', label: 'Modify' },
  { value: 'Dismantle', label: 'Dismantle' },
  { value: 'AllRounder', label: 'All-rounder' },
] as const;

/**
 * Human-readable labels for each availability status — used by the cell badge
 * and the legend at the foot of the grid.
 */
export const crewAvailabilityStatusLabels: Record<CrewAvailabilityStatus, string> = {
  Available: 'Available',
  Booked: 'Booked',
  Off: 'Off',
  OnLeave: 'On leave',
};

/**
 * shadcn Badge variant for each availability status.
 *
 * Centralised here so the grid cell, the crew-member card, and the legend stay
 * visually consistent. Pulls from the variant set declared by
 * `@/shared/ui/badge` (default / success / warning / muted / etc.).
 */
export const crewAvailabilityBadgeVariant: Record<
  CrewAvailabilityStatus,
  'success' | 'default' | 'muted' | 'warning'
> = {
  Available: 'success',
  Booked: 'default',
  Off: 'muted',
  OnLeave: 'warning',
};

/**
 * Quick-assign payload validated by the drawer that opens when a planner taps
 * an "Available" cell.
 *
 * Fields are required (no `.optional()`) so the form resolver input matches
 * its output. Empty strings are used to represent "not yet selected" and the
 * page is responsible for converting them to backend-friendly nulls before
 * the mutation fires.
 */
export const crewQuickAssignSchema = z.object({
  crewMemberId: z.string().min(1, 'Crew member is required'),
  date: z.string().min(1, 'Date is required'),
  activityId: z.string().min(1, 'Activity is required'),
  notes: z.string().max(500, 'Notes must be 500 characters or less').default(''),
});

export type CrewQuickAssignValues = z.infer<typeof crewQuickAssignSchema>;

export const defaultCrewQuickAssignValues: CrewQuickAssignValues = {
  crewMemberId: '',
  date: '',
  activityId: '',
  notes: '',
};
